export const FAQ_DIAGNOSIS_GROUPS = {
  ALL: 'ALL',
  OPEN_ANGLE: 'OPEN_ANGLE',
  SUSPECT: 'SUSPECT',
  PSEUDOEXFOLIATION: 'PSEUDOEXFOLIATION',
}

export const FAQ_GENDER_GROUPS = {
  ALL: 'ALL',
  MALE: 'MALE',
}

export const FAQ_SECTIONS = {
  GENERAL: 'General Q&A',
  LIFESTYLE: 'Q&A About Specific Lifestyle Factors',
  DIET: 'Q&A About Diet',
}

export const FAQ_SECTION_ORDER = [
  FAQ_SECTIONS.GENERAL,
  FAQ_SECTIONS.LIFESTYLE,
  FAQ_SECTIONS.DIET,
]

const t = (text, options = {}) => ({
  text,
  ...options,
})

const citation = (text, href, title) => ({
  text,
  href,
  title,
  citation: true,
})

export const medicationEffectivenessFaq = {
  id: 'medication-effectiveness',
  section: FAQ_SECTIONS.GENERAL,
  question: 'How effective are glaucoma medications?',
  diagnoses: [FAQ_DIAGNOSIS_GROUPS.ALL],
  genders: [FAQ_GENDER_GROUPS.ALL],
  answer: [
    {
      type: 'paragraph',
      content: [t('Very effective.')],
    },
    {
      type: 'bullets',
      items: [
        [
          t('If you have high eye pressure (ocular hypertension)', { bold: true }),
          t(' but have not yet developed glaucoma, and you take medications to lower your eye pressure, your risk of developing glaucoma decreases by '),
          t('53% (from 9% to 4.4%).', { bold: true }),
          citation(
            '1',
            'https://doi.org/10.1001/archopht.120.6.701',
            'The Ocular Hypertension Treatment Study'
          ),
        ],
      ],
    },
    {
      type: 'heading',
      content: [t('For those with ocular hypertension')],
    },
    {
      type: 'image',
      variants: {
        A: {
          src: '/images/faq/effective/prevention.png',
          alt: 'Current graphic explaining that medicated eye drops can cut glaucoma risk in half',
        },
        B: {
          src: '/images/faq/comparison/medication-prevention-round-2.png',
          alt: 'Alternative graphic explaining that medicated eye drops can cut glaucoma risk in half',
        },
      },
    },
    {
      type: 'bullets',
      emphasis: true,
      items: [
        [
          t('If you already have glaucoma,', { bold: true }),
          t(' taking medications consistently reduces the risk of losing even a small amount of vision by '),
          t('58%.', { bold: true }),
          citation(
            '2',
            'https://doi.org/10.1016/j.ophtha.2020.06.009',
            'Risk Factors for Visual Field Deterioration in the United Kingdom Glaucoma Treatment Study'
          ),
        ],
      ],
    },
    {
      type: 'heading',
      content: [t('For those with glaucoma')],
    },
    {
      type: 'image',
      variants: {
        A: {
          src: '/images/faq/effective/treatment.png',
          alt: 'Current graphic explaining the effectiveness of latanoprost',
        },
        B: {
          src: '/images/faq/comparison/medication-treatment-round-2-1.png',
          alt: 'Alternative graphic explaining the effectiveness of latanoprost',
        },
      },
    },
    {
      type: 'paragraph',
      emphasis: true,
      content: [
        t(
          'Even though latanoprost is excellent, sometimes, it alone cannot completely control the glaucoma. It is important to keep following with your doctor to figure out whether you need additional treatments.'
        ),
      ],
    },
  ],
}

export const familyMembersFaq = {
  id: 'family-members',
  section: FAQ_SECTIONS.GENERAL,
  question: 'Should my family members be concerned about glaucoma?',
  diagnoses: [FAQ_DIAGNOSIS_GROUPS.ALL],
  genders: [FAQ_GENDER_GROUPS.ALL],
  answer: [
    {
      type: 'paragraph',
      content: [
        t('Because glaucoma runs in families, relatives may benefit from:'),
      ],
    },
    {
      type: 'bullets',
      items: [
        [t('Regular eye exams')],
        [t('Healthy lifestyle habits shown to reduce glaucoma risk')],
      ],
    },
  ],
}

export const lifestyleFaqs = [
  // {
  //   id: 'lifestyle-affect',
  //   section: FAQ_SECTIONS.GENERAL,
  //   question: 'Does my lifestyle affect glaucoma?',
  //   diagnoses: [FAQ_DIAGNOSIS_GROUPS.ALL],
  //   genders: [FAQ_GENDER_GROUPS.ALL],
  //   answer: [
  //     {
  //       type: 'paragraph',
  //       content: [
  //         t('Lifestyle factors can '),
  //         t('decrease your risk of developing glaucoma.', { bold: true }),
  //         t(' However, once you are diagnosed with glaucoma, lifestyle changes '),
  //         t('do not stop the disease from worsening.', { bold: true }),
  //       ],
  //     },
  //     {
  //       type: 'paragraph',
  //       content: [
  //         t('Scientific studies show that '),
  //         t(
  //           'only treatments that lower eye pressure — medications, laser therapy, or surgery — have been proven to preserve vision',
  //           { bold: true }
  //         ),
  //         t(' in people with glaucoma.'),
  //       ],
  //     },
  //   ],
  // },

  {
    id: 'diet-cure-control',
    section: FAQ_SECTIONS.GENERAL,
    question: 'Can my diet cure or control glaucoma?',
    diagnoses: [FAQ_DIAGNOSIS_GROUPS.ALL],
    genders: [FAQ_GENDER_GROUPS.ALL],
    answer: [
      {
        type: 'paragraph',
        content: [
          t('No. Once glaucoma is present, changing your diet '),
          t('cannot control the disease.', { bold: true }),
          t(' Glaucoma progression is controlled by '),
          t('lowering eye pressure', { bold: true }),
          t(' with medications, laser treatments, and/or surgery.'),
        ],
      },
      {
        type: 'paragraph',
        content: [
          t(
            'Maintaining a healthy lifestyle is still important for overall health and for reducing glaucoma risk in people who do not yet have the disease.'
          ),
        ],
      },
    ],
  },

  {
    id: 'why-doctors-talk-about-lifestyle',
    section: FAQ_SECTIONS.GENERAL,
    question: 'If lifestyle changes don’t control glaucoma, why do doctors talk about them?',
    diagnoses: [FAQ_DIAGNOSIS_GROUPS.ALL],
    genders: [FAQ_GENDER_GROUPS.ALL],
    answer: [
      {
        type: 'paragraph',
        content: [t('Lifestyle changes are most helpful for:')],
      },
      {
        type: 'bullets',
        items: [
          [t('Reducing the risk of developing glaucoma')],
          [t('Counseling family members who are at a higher risk')],
        ],
      },
      {
        type: 'paragraph',
        content: [
          t('First-degree relatives of people with glaucoma have an '),
          t('8-fold higher risk', { bold: true }),
          t(' of developing the disease.'),
        ],
      },
    ],
  },

  {
    id: 'alcohol',
    section: FAQ_SECTIONS.LIFESTYLE,
    question: 'Does alcohol affect glaucoma risk?',
    diagnoses: [FAQ_DIAGNOSIS_GROUPS.ALL],
    genders: [FAQ_GENDER_GROUPS.ALL],
    answer: [
      {
        type: 'paragraph',
        content: [
          t('For most people, alcohol '),
          t('does not increase glaucoma risk.', { bold: true }),
        ],
      },
      {
        type: 'paragraph',
        content: [t('Large population-based study shows:')],
      },
      {
        type: 'bullets',
        items: [
          [
            t('Among those who drink at least one drink per week, those who drink more than '),
            t('4 alcoholic drinks per week', { bold: true }),
            t(' have higher odds of glaucoma, higher eye pressure, and thinner tissue feeding into the optic nerve.'),
            citation(
              '3',
              'https://doi.org/10.1016/j.ogla.2022.11.008',
              'The Association of Alcohol Consumption with Glaucoma and Related Traits: Findings from the UK Biobank'
            ),
          ],
          [
            t('Each additional 8 drinks per week is associated with an '),
            t('11% increase in glaucoma risk.', { bold: true }),
          ],
          [
            t('This effect is strongest in people with '),
            t('high genetic susceptibility to glaucoma.', { bold: true }),
          ],
        ],
      },
      {
        type: 'paragraph',
        content: [
          t('What this means: '),
          t(
            'People who have glaucoma that is hard to control or are at an increased risk for glaucoma may want to moderate alcohol intake.',
            { bold: true }
          ),
        ],
      },
    ],
  },

  {
    id: 'caffeine-general',
    section: FAQ_SECTIONS.LIFESTYLE,
    question: 'Should I avoid caffeine?',
    diagnoses: [FAQ_DIAGNOSIS_GROUPS.ALL],
    excludeDiagnoses: [FAQ_DIAGNOSIS_GROUPS.PSEUDOEXFOLIATION],
    genders: [FAQ_GENDER_GROUPS.ALL],
    answer: [
      {
        type: 'paragraph',
        content: [
          t('For most people, caffeine '),
          t('does not increase glaucoma risk.', { bold: true }),
          citation(
            '4',
            'https://doi.org/10.1016/j.ophtha.2020.12.009',
            'Intraocular Pressure, Glaucoma, and Dietary Caffeine Consumption: A Gene-Diet Interaction Study from the UK Biobank'
          ),
        ],
      },
      {
        type: 'paragraph',
        content: [t('However:')],
      },
      {
        type: 'bullets',
        items: [
          [
            t('Among people in the highest 20% of genetic risk for high eye pressure based on DNA testing, those with the highest caffeine intake (more than 3 cups of coffee per day/greater than 320 mg per day) had a '),
            t('30% higher chance of glaucoma', { bold: true }),
            t(' compared with those consuming less than one cup per day (about 80 mg per day or less).'),
          ],
          [
            t('Genetic testing in glaucoma is currently only available in research settings and may be available to be used in clinical settings in the future.'),
          ],
        ],
      },
      {
        type: 'paragraph',
        content: [
          t('What this means: '),
          t(
            'If eye pressure is high or hard to control, or if glaucoma runs in your family, moderating caffeine intake may be reasonable.',
            { bold: true }
          ),
        ],
      },
    ],
  },

  {
    id: 'caffeine-pseudoexfoliation',
    section: FAQ_SECTIONS.LIFESTYLE,
    question: 'Should I avoid caffeine?',
    diagnoses: [FAQ_DIAGNOSIS_GROUPS.PSEUDOEXFOLIATION],
    genders: [FAQ_GENDER_GROUPS.ALL],
    answer: [
      {
        type: 'bullets',
        items: [
          [
            t('High intake of caffeinated coffee '),
            t('(3 or more cups per day)', { bold: true }),
            t(' is associated with a '),
            t('higher risk of pseudoexfoliation glaucoma', { bold: true }),
            citation(
              '5',
              'https://doi.org/10.1167/iovs.12-10085',
              'The Relationship between Caffeine and Coffee Consumption and Exfoliation Glaucoma or Glaucoma Suspect'
            ),
          ],
          [
            t('This effect is strongest in '),
            t('women with a family history of glaucoma', { bold: true }),
          ],
          [
            t('Other caffeinated beverages '),
            t('made no difference on glaucoma risk', { bold: true }),
          ],
        ],
      },
    ],
  },

  {
    id: 'smoking',
    section: FAQ_SECTIONS.LIFESTYLE,
    question: 'Does smoking increase glaucoma risk?',
    diagnoses: [
      FAQ_DIAGNOSIS_GROUPS.OPEN_ANGLE,
      FAQ_DIAGNOSIS_GROUPS.SUSPECT,
    ],
    genders: [FAQ_GENDER_GROUPS.ALL],
    answer: [
      {
        type: 'paragraph',
        content: [
          t('Studies show '),
          t('no increased risk of glaucoma from smoking', { bold: true }),
          t(', though avoiding smoking is better for overall and eye health.'),
          citation(
            '6',
            'https://doi.org/10.1001/archopht.121.12.1762',
            'Prospective Study of Cigarette Smoking and the Risk of Primary Open-Angle Glaucoma'
          ),
        ],
      },
      {
        type: 'paragraph',
        content: [
          t('However, smoking does increase the risk of macular degeneration.'),
          citation(
            '7',
            'https://doi.org/10.1155/2013/895147',
            'Smoking and Age-Related Macular Degeneration: Review and Update'
          ),
        ],
      },
    ],
  },

  {
    id: 'physical-fitness',
    section: FAQ_SECTIONS.LIFESTYLE,
    question: 'Does being physically fit matter?',
    diagnoses: [FAQ_DIAGNOSIS_GROUPS.ALL],
    genders: [FAQ_GENDER_GROUPS.ALL],
    answer: [
      {
        type: 'paragraph',
        content: [
          t(
            'Yes. In a large population study, men with the following strengths were less likely to have glaucoma and more likely to have healthier optic nerve tissue.'
          ),
          citation(
            '8',
            'https://doi.org/10.1167/iovs.66.6.66',
            'The Association of Muscle-Related Factors With Glaucoma and Related Traits in a Large United Kingdom Population'
          ),
          t(
            ' There is no good data yet on physical fitness for women.',
            { bold: true }
          ),
        ],
      },
      {
        type: 'bullets',
        items: [
          [t('Greater thigh muscle mass', { bold: true })],
          [t('Stronger grip strength', { bold: true })],
          [t('Faster walking pace', { bold: true })],
        ],
      },
      {
        type: 'paragraph',
        content: [
          t('What this means: '),
          t('Physical activity and fitness may support optic nerve health.', {
            bold: true,
          }),
        ],
      },
    ],
  },

  {
    id: 'foods-prevent',
    section: FAQ_SECTIONS.DIET,
    question: 'Are there foods that prevent glaucoma?',
    diagnoses: [FAQ_DIAGNOSIS_GROUPS.ALL],
    genders: [FAQ_GENDER_GROUPS.ALL],
    answer: [
      {
        type: 'paragraph',
        content: [
          t('Some foods may '),
          t('lower the risk of developing glaucoma', { bold: true }),
          t(', but they '),
          t('do not treat glaucoma once it is present.', { bold: true }),
        ],
      },
    ],
  },

  {
    id: 'leafy-greens',
    section: FAQ_SECTIONS.DIET,
    question: 'Do leafy greens help?',
    diagnoses: [
      FAQ_DIAGNOSIS_GROUPS.OPEN_ANGLE,
      FAQ_DIAGNOSIS_GROUPS.SUSPECT,
    ],
    genders: [FAQ_GENDER_GROUPS.ALL],
    answer: [
      {
        type: 'paragraph',
        content: [t('Yes — for prevention.', { bold: true })],
      },
      {
        type: 'paragraph',
        content: [
          t('Long-term studies found that people who ate the most leafy green vegetables had about a '),
          t('30% lower risk', { bold: true }),
          t(' of developing open-angle glaucoma compared with those who ate the least.'),
          citation(
            '9',
            'https://doi.org/10.1001/jamaophthalmol.2015.5601',
            'Association of Dietary Nitrate Intake With Primary Open-Angle Glaucoma'
          ),
        ],
      },
      {
        type: 'paragraph',
        content: [t('Examples include:')],
      },
      {
        type: 'bullets',
        items: [
          [t('Lettuce, kale, chard, and mustard greens')],
          [
            t(
              'Beets are also a great source of nitrates, just like greens. Nitrates are what we think are the active ingredient here in reducing glaucoma risk.'
            ),
          ],
        ],
      },
      {
        type: 'paragraph',
        content: [
          t('The study was adjusted for the following factors: body mass index, total calories, alcohol, caffeine, smoking, vitamin use, physical activity, age, ancestry, diabetes, hypertension, and number of eye exams.'),
        ],
      },
    ],
  },

  {
    id: 'fats',
    section: FAQ_SECTIONS.DIET,
    question: 'Do fats matter?',
    diagnoses: [
      FAQ_DIAGNOSIS_GROUPS.OPEN_ANGLE,
      FAQ_DIAGNOSIS_GROUPS.SUSPECT,
    ],
    genders: [FAQ_GENDER_GROUPS.ALL],
    answer: [
      {
        type: 'paragraph',
        content: [
          t('Yes. Diets with a '),
          t('higher ratio of omega-6 to omega-3 polyunsaturated fats', {
            bold: true,
          }),
          t(' are associated with a '),
          t('lower risk of developing glaucoma.', { bold: true }),
          citation(
            '10',
            'https://doi.org/10.1093/ajcn/79.5.755',
            'Dietary Fat Consumption and Primary Open-Angle Glaucoma'
          ),
        ],
      },
      {
        type: 'paragraph',
        content: [t('Examples include:')],
      },
      {
        type: 'bullets',
        items: [
          [t('Avocados')],
          [t('Grape seed oil')],
          [t('High-oleic sunflower or safflower oil')],
          [t('Peanuts and sesame')],
        ],
      },
    ],
  },

  {
    id: 'supplements-general',
    section: FAQ_SECTIONS.DIET,
    question: 'Do antioxidants or supplements prevent glaucoma?',
    diagnoses: [FAQ_DIAGNOSIS_GROUPS.ALL],
    excludeDiagnoses: [FAQ_DIAGNOSIS_GROUPS.PSEUDOEXFOLIATION],
    genders: [FAQ_GENDER_GROUPS.ALL],
    answer: [
      {
        type: 'paragraph',
        content: [
          t('Extensive research has found '),
          t('no consistent protective effect', { bold: true }),
          t(' from'),
          citation(
            '11',
            'https://doi.org/10.1093/aje/kwg167',
            'Antioxidant Intake and Primary Open-Angle Glaucoma: a Prospective Study'
          ),
          t(':'),
        ],
      },
      {
        type: 'bullets',
        items: [
          [t('Antioxidants (vitamins A, C, E, carotenoids)')],
          [t('Flavonoids (berries, citrus, tea, wine, cocoa)')],
          [t('Branched-chain amino acids')],
          [t('Dietary fat overall')],
        ],
      },
    ],
  },

  {
    id: 'supplements-pseudoexfoliation',
    section: FAQ_SECTIONS.DIET,
    question: 'Do antioxidants or supplements prevent glaucoma?',
    diagnoses: [FAQ_DIAGNOSIS_GROUPS.PSEUDOEXFOLIATION],
    genders: [FAQ_GENDER_GROUPS.ALL],
    answer: [
      {
        type: 'paragraph',
        content: [
          t('Extensive research has found '),
          t('no consistent protective effect', { bold: true }),
          t(' from'),
          citation(
            '11',
            'https://doi.org/10.1093/aje/kwg167',
            'Antioxidant Intake and Primary Open-Angle Glaucoma: a Prospective Study'
          ),
          t(':'),
        ],
      },
      {
        type: 'bullets',
        items: [
          [t('Antioxidants (vitamins A, C, E, carotenoids)')],
          [t('Flavonoids (berries, citrus, tea, wine, cocoa)')],
          [t('Branched-chain amino acids')],
          [t('Dietary fat overall')],
        ],
      },
      {
        type: 'paragraph',
        content: [
          t('However, a '),
          t('higher intake of folate', { bold: true }),
          t(' is associated with a '),
          t('lower risk of developing pseudoexfoliation glaucoma.', {
            bold: true,
          }),
          citation(
            '11',
            'https://doi.org/10.1001/jamaophthalmol.2014.100',
            'A Prospective Study of Folate, Vitamin B6, and Vitamin B12 Intake in Relation to Exfoliation Glaucoma or Suspected Exfoliation Glaucoma'
          ),
        ],
      },
    ],
  },
]

const diagnosisToFaqGroup = diagnosis => {
  switch (diagnosis) {
    case 'OPEN_ANGLE':
      return FAQ_DIAGNOSIS_GROUPS.OPEN_ANGLE
    case 'SUSPECT':
      return FAQ_DIAGNOSIS_GROUPS.SUSPECT
    case 'PSEUDOEXFOLIATION':
      return FAQ_DIAGNOSIS_GROUPS.PSEUDOEXFOLIATION
    default:
      return FAQ_DIAGNOSIS_GROUPS.ALL
  }
}

const genderToFaqGroup = gender => {
  switch (gender) {
    case 'MALE':
      return FAQ_GENDER_GROUPS.MALE
    default:
      return FAQ_GENDER_GROUPS.ALL
  }
}

export const getVisibleLifestyleFaqs = (diagnosis, gender) => {
  const diagnosisGroup = diagnosisToFaqGroup(diagnosis)
  const genderGroup = genderToFaqGroup(gender)

  return lifestyleFaqs.filter(item => {
    const diagnosisMatches =
      item.diagnoses.includes(FAQ_DIAGNOSIS_GROUPS.ALL) ||
      item.diagnoses.includes(diagnosisGroup)

    const diagnosisExcluded =
      item.excludeDiagnoses &&
      item.excludeDiagnoses.includes(diagnosisGroup)

    const genderMatches =
      item.genders.includes(FAQ_GENDER_GROUPS.ALL) ||
      item.genders.includes(genderGroup)

    return diagnosisMatches && !diagnosisExcluded && genderMatches
  })
}



// WEBPACK FOOTER //
// ./src/utils/lifeStyleFaqs.js